import { Alert, Badge, Button, Group, Stack, Table, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EmptyNote } from '../../components/RecordScreen';
import { enumColor, enumLabel } from '../../lib/enums';
import { formatMicroQty } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';

export const WHAT_CAN_I_MAKE_PATH = '/admin/v1/inventory/what-can-i-make';

/** The part that is the same question whether the row is a dish, a choice or an extra. */
interface Answer {
  nameEn: string;
  servings: number | null;
  basis: string;
  limitedBy: {
    inventoryItemId: string;
    nameEn: string;
    unit: string;
    onHandMicro: number;
    onHandAtPourPlaceMicro: number;
    perServingMicro: number;
  } | null;
  needsFetching: boolean;
  faults: { reason: string; componentNameEn: string | null; detail: string | null }[];
}

/**
 * A choice the guest cannot decline, when what is left of it is what stops the dish.
 *
 * Arrives instead of `limitedBy` and never beside it: no single ingredient is the answer when the
 * fish and the prawns cover ten tacos between them, and the tortilla — of which there are forty —
 * would be the worst possible thing to put in that column.
 */
interface LimitingChoice {
  modifierGroupId: string;
  nameEn: string;
  optionCount: number;
}

interface Servable extends Answer {
  variantId: string;
  /** The dish. Taking it off the board takes every size of it off, which the button says. */
  menuItemId: string;
  eightySixed: boolean;
  limitedByChoice: LimitingChoice | null;
}

/**
 * One option — "Add bacon", or "Fish" out of a protein the guest has to pick.
 *
 * No board state of its own: taking a dish off because an optional extra ran out is exactly the
 * fold the tables exist to refuse, and there is no way to make one OPTION unavailable today.
 */
type ServableExtra = Answer & {
  modifierOptionId: string;
  /** The guest has to pick one of its set, so this is a choice and its number is not optional. */
  required: boolean;
};

type ServableAnything = Answer & {
  key: string;
  /** Present on a dish, absent on an option: only a dish can be taken off the board. */
  board?: { menuItemId: string; eightySixed: boolean };
  /** Present on a dish. An option cannot be limited by a choice; it IS one. */
  limitedByChoice?: LimitingChoice | null;
};

/**
 * Taking a dish off the board from the screen that just told you it cannot be made.
 *
 * This is the whole of "running out is seen and not acted on" that belongs to this side of the
 * house. The toggle, the audit entry and the push to every terminal on the floor were all built
 * already; what was missing was that the one screen that KNOWS a dish is unservable had no way to
 * say so, so the manager read a nought here and then went hunting for the dish on the menu screen —
 * which on a Saturday means he does not.
 *
 * It is a decision and not an automation, on purpose. See the note at the foot of this file.
 */
function BoardButton({
  menuItemId,
  eightySixed,
  nameEn,
}: {
  menuItemId: string;
  eightySixed: boolean;
  nameEn: string;
}) {
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: () =>
      rawApi('POST', `/admin/v1/catalog/items/${menuItemId}/eighty-six`, {
        eightySixed: !eightySixed,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey(WHAT_CAN_I_MAKE_PATH) });
      notifyDone(
        eightySixed ? 'Back on the menu' : 'Taken off the menu',
        eightySixed
          ? `${nameEn} can be ordered again. Every register has been told.`
          : `${nameEn} cannot be ordered. Every register has been told, and tickets already sent to the kitchen are untouched.`,
      );
    },
    onError: (err) => notifyError(err, eightySixed ? 'put that back on' : 'take that off'),
  });

  return (
    <Button
      size="compact-xs"
      variant={eightySixed ? 'light' : 'subtle'}
      color={eightySixed ? 'green' : 'red'}
      loading={toggle.isPending}
      onClick={() => toggle.mutate()}
    >
      {eightySixed ? 'Put back on' : 'Take off the menu'}
    </Button>
  );
}

/**
 * The five columns, whether the thing in the first one is a dish or an extra.
 *
 * One component because the two tables answer the same question about different nouns, and a
 * second copy would be a second place for "0 is red", "a basis other than complete wears a badge"
 * and "the fetching note goes under the limiting ingredient" to drift apart.
 */
function ServableTable({ heading, rows }: { heading: string; rows: ServableAnything[] }) {
  return (
    <Table.ScrollContainer minWidth={860}>
      <Table verticalSpacing="sm" highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{heading}</Table.Th>
            <Table.Th ta="right">How many</Table.Th>
            <Table.Th>What runs out first</Table.Th>
            <Table.Th ta="right">One takes</Table.Th>
            <Table.Th ta="right">On hand</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.key}>
              <Table.Td>
                <Group gap={8} wrap="nowrap">
                  <Text fw={500}>{row.nameEn}</Text>
                  {row.board?.eightySixed && (
                    <Badge color="red" variant="light" style={{ textTransform: 'none' }}>
                      Off the menu
                    </Badge>
                  )}
                </Group>
                {row.faults.map((f) => (
                  <Text size="xs" c="orange.7" key={`${f.reason}-${f.componentNameEn ?? ''}`}>
                    {f.detail ?? enumLabel('depletionFault', f.reason)}
                  </Text>
                ))}
                {/* Offered where the nought is, not on a screen two clicks away. Only where there
                    is something to decide: a dish with plenty on the shelf does not need a button
                    that takes it off, and a row of them would make the one that matters invisible. */}
                {row.board && (row.servings === 0 || row.board.eightySixed) && (
                  <Group gap={8} mt={4}>
                    <BoardButton
                      menuItemId={row.board.menuItemId}
                      eightySixed={row.board.eightySixed}
                      nameEn={row.nameEn}
                    />
                    <Text size="xs" c="dimmed">
                      Every size of it, on every register.
                    </Text>
                  </Group>
                )}
              </Table.Td>
              <Table.Td ta="right">
                <Group gap={6} justify="flex-end">
                  <Text fw={700} c={row.servings === 0 ? 'red.7' : undefined}>
                    {row.servings}
                  </Text>
                  {row.basis !== 'complete' && (
                    <Badge
                      color={enumColor('servingsBasis', row.basis) ?? 'orange'}
                      variant="light"
                      style={{ textTransform: 'none' }}
                    >
                      {enumLabel('servingsBasis', row.basis)}
                    </Badge>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>
                {/*
                  AN INGREDIENT WHERE ONE RAN OUT, A CHOICE WHERE ONE DID, AND SOMETIMES BOTH.
                  Where the choice settled the figure there is no ingredient to name and naming one
                  would be a lie: the dish's own scarcest — forty tortillas — is not what stopped it,
                  and no single protein is either. Where the dish's ingredient still runs out first
                  and the choice only made the figure a ceiling, both lines belong, because a hedge
                  with no reason on the row is one nobody can act on.
                */}
                {row.limitedBy ? (
                  <Text size="sm">{row.limitedBy.nameEn}</Text>
                ) : row.limitedByChoice ? (
                  <Text size="sm">
                    {row.servings === 0
                      ? `Every choice of ${row.limitedByChoice.nameEn} has run out`
                      : `What ${row.limitedByChoice.nameEn} covers`}
                  </Text>
                ) : (
                  <Text size="sm" c="dimmed">
                    —
                  </Text>
                )}
                {row.limitedByChoice && (
                  <Text size="xs" c="dimmed">
                    One of its {row.limitedByChoice.optionCount} choices goes on every plate
                    {row.limitedBy
                      ? ', and no one of them covers this many on its own.'
                      : ', and that is what they cover between them.'}
                  </Text>
                )}
                {(row.limitedBy || row.limitedByChoice) && row.needsFetching && (
                  <Text size="xs" c="blue.7">
                    Not at the area a sale takes it from — bring some through.
                  </Text>
                )}
              </Table.Td>
              <Table.Td ta="right">
                {row.limitedBy ? formatMicroQty(row.limitedBy.perServingMicro, row.limitedBy.unit) : '—'}
              </Table.Td>
              <Table.Td ta="right">
                {row.limitedBy ? formatMicroQty(row.limitedBy.onHandMicro, row.limitedBy.unit) : '—'}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

/**
 * How many of each thing on the menu the shelf will actually support, asked before service.
 *
 * The on-hand table answers how much of everything there is. Nobody serves a shelf. What is wanted
 * at four in the afternoon is how many mojitos and how many burgers can go out tonight, and which
 * ingredient is the one that stops each of them — which is the same arithmetic that runs when
 * somebody rings the drink up, run against what is on hand instead of against what was sold.
 *
 * ## Three answers, and the difference between them is the point
 *
 * A dish nobody has written a recipe for consumes nothing, and a screen that divides the shelf by
 * nothing says you can make an unlimited number of them — on the exact screen somebody is using to
 * decide whether to send a driver to the mainland. So it says **it cannot be worked out**, and says
 * what is missing. A recipe that could be worked out only in part gives a CEILING and reads as one:
 * the ingredient nobody could account for is exactly as likely as any other to be the one that runs
 * out first, so the number can only come down.
 *
 * ## The extras answer their own question
 *
 * "Add bacon" comes off a shelf like anything else, and this screen used to walk dishes only — so a
 * kitchen that had run out of bacon at lunch looked completely healthy at four. It is a separate
 * list rather than a number folded into the burger, and that is deliberate: an extra is OPTIONAL. A
 * burger whose bacon has run out is still a burger, and a screen that let the bacon set the
 * burger's number would take three dozen saleable burgers off the board over a garnish.
 *
 * ## A choice the guest cannot decline is a third table, and the sentence under it is the opposite
 *
 * "Optional" carries that whole argument, and a set of options is only sometimes optional. A taco
 * that has to have a protein is not a taco once every protein has run out, however many tortillas
 * are on the shelf — so a required set counts towards the dish and an optional one never does, and
 * the two are told apart by what the menu says rather than by how the options read.
 *
 * The figure it contributes is what all its options cover BETWEEN them, because the guest can pick
 * another one: six fish and four prawns is ten tacos, not six. Whether that is the answer or only a
 * ceiling turns on whether reaching it needs the options mixed and whether they compete for a shelf —
 * twenty-five fish and twenty-five prawns is forty tacos exactly, while a single and a double portion
 * out of six of the same shrimp is never the nine the arithmetic offers. The second says "at most",
 * and the first says nothing, because a badge on every row is a badge nobody reads.
 *
 * Its own table and not a note on the dish, because the dish's row can only name the SET. Which
 * protein has actually run out is the thing somebody has to act on, and this is the only list that
 * says.
 *
 * ## Seeing it and acting on it, and why acting is a BUTTON and not a rule
 *
 * A row that reads nought used to be the end of the road: the manager read it here and then went
 * looking for the dish on the menu screen to take it off, which on a Saturday afternoon means he
 * does not. The toggle, the audit trail and the push to every terminal were all built; the screen
 * that knew was the one screen that could not say so. So the button is here, on the row.
 *
 * It is deliberately NOT automatic, and that is a judgement rather than an omission:
 *
 *   - The figure it would fire on is the BOOK figure, and this whole module exists around the fact
 *     that the book drifts from the shelf between counts. Taking a dish off the menu because the
 *     book says nought is refusing to sell something that is standing on the shelf, silently, at
 *     the busiest hour — a loss nobody can see afterwards, because a dish that was never offered
 *     leaves no record of not being sold.
 *   - Anything that takes a dish off automatically has to put it back automatically, or the menu
 *     shrinks all week and never grows. And putting it back automatically would override the
 *     owner's own hand: he takes the snapper off because it looked tired, a delivery lands, and the
 *     registers start selling it again. `menu_items.eightySixed` is one boolean and cannot tell "the
 *     kitchen ran out" from "the owner said no" — which is a schema decision, and it belongs with
 *     the register that reads the flag rather than being guessed at from here.
 *
 * ## What it does not claim
 *
 * Every row is "if you made only this one". Two cocktails sharing the rum will each report what the
 * rum allows, and they cannot both be true at once. That is the honest shape for the question being
 * asked — what to take off the board tonight — and not a production plan.
 */
export function WhatCanIMake() {
  const [filter, setFilter] = useState('');
  const rows = useQuery({
    queryKey: listKey(WHAT_CAN_I_MAKE_PATH),
    queryFn: () => rawApi<{ rows: Servable[]; extras: ServableExtra[] }>('GET', WHAT_CAN_I_MAKE_PATH),
  });

  const all = rows.data?.rows ?? [];
  const allExtras = rows.data?.extras ?? [];
  const needle = filter.trim().toLowerCase();
  const matches = (name: string) => !needle || name.toLowerCase().includes(needle);
  const list = all.filter((r) => matches(r.nameEn));
  const extras = allExtras.filter((r) => matches(r.nameEn));
  /*
   * THE ANSWERS FIRST, and the rest under their own heading.
   *
   * These arrive with the unanswerable rows at the top, on the reasoning that "we cannot tell you"
   * is the more urgent sentence before service. On a menu forty-eight dishes long with three
   * recipes written, that put forty-five rows of "Cannot say" — and three columns of dashes —
   * between the owner and the one number on the page. The question this screen exists to answer at
   * four in the afternoon is what can go out tonight, and it has to answer it in one look. Nothing
   * is hidden: the count is in the warning above, and every one of them is listed below with what
   * is missing from it.
   */
  const servable = list.filter((r) => r.servings !== null);
  /*
   * TWO KINDS OF OPTION, AND THE HEADING OVER THEM IS THE WHOLE POINT.
   *
   * An extra is optional, so its number stands alone and the dishes above are untouched by it. A
   * choice the guest cannot decline — which protein goes in the taco — is part of every plate, so
   * what is left of it is already inside the dishes above. Same five columns, opposite sentence
   * underneath, and putting a choice under the extras' sentence would tell the owner that running
   * out of every protein does not stop the taco.
   */
  const servableExtras = extras.filter((r) => r.servings !== null && !r.required);
  const servableChoices = extras.filter((r) => r.servings !== null && r.required);
  // The extras join the same "cannot be worked out" list. An option whose recipe will not explode
  // is the identical gap in the identical place, and two lists saying the same sentence about two
  // kinds of row is two lists nobody reads to the bottom of.
  const unanswerable = [
    ...list.filter((r) => r.servings === null).map((r) => ({ key: r.variantId, ...r })),
    ...extras.filter((r) => r.servings === null).map((r) => ({ key: r.modifierOptionId, ...r })),
  ];
  const unknown = [...all, ...allExtras].filter((r) => r.servings === null).length;
  const toFetch = [...all, ...allExtras].filter((r) => r.needsFetching).length;

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Text size="sm" c="dimmed" maw={820}>
          How many of each thing on the menu the stock on hand will support, worked out through the same
          recipes a sale uses. Each row assumes you made only that one — two drinks sharing the rum will each
          tell you what the rum allows, and they cannot both be true at once. A choice the guest has to make
          counts towards the dish, because every plate carries one; an optional extra does not, because a
          burger with no bacon left is still a burger.
        </Text>
      </Stack>

      {rows.isError && <Alert color="red">That list could not be loaded.</Alert>}

      {unknown > 0 && (
        <Alert color="orange" title={`${unknown} of these cannot be worked out`}>
          Nothing says what they are made of, or an ingredient in them cannot be traced to a shelf, so there
          is no honest number to put beside them. They are listed under the table, each with what is missing.
          Writing the recipe fixes this everywhere at once — the same gap is why they cost nothing to make and
          move no stock when they sell.
        </Alert>
      )}

      {toFetch > 0 && (
        <Alert color="blue" title={`${toFetch} of these need something bringing through`}>
          There is enough in the building, but not in the area a sale takes it from. Move it before service
          rather than at nine on a Saturday.
        </Alert>
      )}

      <Group>
        <TextInput
          placeholder="Find a dish, drink, choice or extra"
          value={filter}
          onChange={(e) => setFilter(e.currentTarget.value)}
          w={260}
        />
      </Group>

      {!rows.isLoading && all.length === 0 && (
        <EmptyNote>Nothing is on the menu yet, so there is nothing to work out.</EmptyNote>
      )}
      {!rows.isLoading && all.length > 0 && list.length === 0 && (
        <EmptyNote>Nothing on the menu matches that.</EmptyNote>
      )}

      {servable.length > 0 && (
        <ServableTable
          heading="Dish or drink"
          rows={servable.map((r) => ({
            ...r,
            key: r.variantId,
            board: { menuItemId: r.menuItemId, eightySixed: r.eightySixed },
          }))}
        />
      )}

      {!rows.isLoading && list.length > 0 && servable.length === 0 && (
        <EmptyNote>
          Nothing on the menu can be worked out yet. Write what these are made of and this becomes the list of
          what can go out tonight.
        </EmptyNote>
      )}

      {/*
        THE EXTRAS, UNDER THEIR OWN HEADING AND NOT INSIDE THE DISHES' NUMBERS.

        An extra is optional, so bacon that has run out does not stop the burger being sold — it
        stops you saying yes to bacon. Letting it set the burger's figure would take every burger
        off the board over a garnish, which is the more expensive of the two mistakes. The sentence
        under the heading is the whole reason there are two tables and it has to be on the screen:
        a manager who reads this as "six burgers left" has been misled by our layout.
      */}
      {servableExtras.length > 0 && (
        <Stack gap={4} mt="lg">
          <Text fw={600}>Extras</Text>
          <Text size="sm" c="dimmed" maw={820} mb={4}>
            How many times you can say yes to each of these. An extra running out does not stop the dish being
            sold — it stops that dish being sold with the extra on it, so these numbers stand on their own and
            are never taken off the figures above.
          </Text>
          <ServableTable
            heading="Extra"
            rows={servableExtras.map((r) => ({ key: r.modifierOptionId, ...r }))}
          />
        </Stack>
      )}

      {/*
        THE CHOICES, WHICH ARE NOT EXTRAS AND CARRY THE OPPOSITE SENTENCE.
        Listed because "which protein have I run out of" is a question nothing else on this screen
        answers — the dish row above can only name the set. The number beside each one is still "if
        you served only this", and unlike an extra it is already inside the dishes above.
      */}
      {servableChoices.length > 0 && (
        <Stack gap={4} mt="lg">
          <Text fw={600}>Choices the guest has to make</Text>
          <Text size="sm" c="dimmed" maw={820} mb={4}>
            One of each of these sets goes on every plate, so these are not extras: what is left of them is
            already inside the figures above, and a dish whose every choice has run out cannot go out at all.
            Each number is how many you could serve if you picked that one every time.
          </Text>
          <ServableTable
            heading="Choice"
            rows={servableChoices.map((r) => ({ key: r.modifierOptionId, ...r }))}
          />
        </Stack>
      )}

      {/*
        Their own list, and only the two columns that say anything about them: the three numeric
        ones read "—" on every single row, which is a column that costs the reader width and tells
        him nothing. "Cannot say" is not a quantity and does not belong in a quantity column.
      */}
      {unanswerable.length > 0 && (
        <Stack gap={4} mt="lg">
          <Text fw={600}>These cannot be worked out</Text>
          <Text size="sm" c="dimmed" maw={820} mb={4}>
            Selling one of these moves nothing off any shelf, so nothing above accounts for what it uses.
            Write the recipe and it joins the list above.
          </Text>
          <Table.ScrollContainer minWidth={620}>
            <Table verticalSpacing="xs" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Dish, drink, choice or extra</Table.Th>
                  <Table.Th>What is missing</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {unanswerable.map((row) => (
                  <Table.Tr key={row.key}>
                    <Table.Td>
                      <Text fw={500}>{row.nameEn}</Text>
                    </Table.Td>
                    <Table.Td>
                      {row.faults.length === 0 ? (
                        <Text size="sm" c="dimmed">
                          Nothing says what this is made of.
                        </Text>
                      ) : (
                        row.faults.map((f) => (
                          <Text size="sm" c="orange.7" key={`${f.reason}-${f.componentNameEn ?? ''}`}>
                            {f.detail ?? enumLabel('depletionFault', f.reason)}
                          </Text>
                        ))
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Stack>
      )}
    </Stack>
  );
}
